import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, FileText, Upload, Plus, LogOut, Home, Settings, 
  ChevronRight, Search, Filter, MoreVertical, Download,
  User, Mail, Phone, Calendar, Clock, CheckCircle, AlertCircle,
  Trash2, Edit, Eye, X, Loader2, CreditCard, Euro, Menu, UserCheck, HelpCircle, CalendarDays, UserX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { downloadReceipt } from '../lib/receipt';
import { AdminAttiTab } from '../components/AdminAttiTab';
import { AdminAccessTab } from '../components/AdminAccessTab';
import { ChatStatsChart } from '../components/ChatStatsChart';
import { ChatTopicsPanel } from '../components/ChatTopicsPanel';
import { AdminFaqTab } from '../components/AdminFaqTab';
import { AdminAgendaTab } from '../components/AdminAgendaTab';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { AppointmentField } from '../components/AppointmentField';
import { PasswordInput } from '../components/PasswordInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'aperta', label: 'Aperta', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_lavorazione', label: 'In Lavorazione', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'da_firmare', label: 'Da Firmare', color: 'bg-orange-100 text-orange-700' },
  { value: 'completata', label: 'Completata', color: 'bg-green-100 text-green-700' },
  { value: 'archiviata', label: 'Archiviata', color: 'bg-slate-100 text-slate-700' }
];

const tipoOptions = [
  'Compravendita', 'Successione', 'Donazione', 'Costituzione Società', 
  'Modifica Societaria', 'Procura', 'Mutuo', 'Autentica', 'Altro'
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [clients, setClients] = useState([]);
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showClientModal, setShowClientModal] = useState(false);
  const [showPraticaModal, setShowPraticaModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedPratica, setSelectedPratica] = useState(null);
  const [documents, setDocuments] = useState([]);
  
  // Forms
  const [clientForm, setClientForm] = useState({ email: '', password: '', name: '', phone: '', role: 'client', tipo_soggetto: 'privato', codice_fiscale: '', partita_iva: '' });
  const [attiPending, setAttiPending] = useState(0);
  const [requestsPending, setRequestsPending] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [praticaForm, setPraticaForm] = useState({ client_id: '', titolo: '', tipo: '', descrizione: '', note_cliente: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');
  
  // Payments
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ pratica_id: '', description: '', amount: '' });

  const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!storedUser || !token) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== 'admin') {
      navigate('/area-clienti');
      return;
    }
    
    setUser(userData);
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, praticheRes, paymentsRes, attiRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/clients`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/pratiche`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/payments`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/atti`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
      setPratiche(praticheRes.data);
      setPayments(paymentsRes.data);
      setAttiPending(attiRes.data.filter((a) => !a.reviewed || a.soggetti.some((s) => s.status === 'da_completare')).length);
      axios.get(`${API}/admin/access-requests`, { headers: getAuthHeader() })
        .then((r) => setRequestsPending(r.data.filter((x) => x.status === 'in_attesa').length)).catch(() => {});
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const emptyClient = { email: '', password: '', name: '', phone: '', role: 'client', tipo_soggetto: 'privato', codice_fiscale: '', partita_iva: '' };
  const [editingClientId, setEditingClientId] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredClients = clients.filter((c) => {
    const q = norm(clientSearch.trim());
    return !q || [c.name, c.email, c.codice_fiscale, c.partita_iva, c.phone].some((v) => norm(v).includes(q));
  });
  const praticheCount = pratiche.reduce((acc, p) => ({ ...acc, [p.client_id]: (acc[p.client_id] || 0) + 1 }), {});

  const openEditClient = (c) => {
    setClientForm({ ...emptyClient, email: c.email, name: c.name, phone: c.phone || '', tipo_soggetto: c.tipo_soggetto || 'privato', codice_fiscale: c.codice_fiscale || '', partita_iva: c.partita_iva || '' });
    setEditingClientId(c.id);
    setShowClientModal(true);
  };

  const closeClientModal = () => {
    setShowClientModal(false);
    setEditingClientId(null);
    setClientForm(emptyClient);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const payload = clientForm.tipo_soggetto === 'azienda' ? clientForm : { ...clientForm, partita_iva: '' };
      if (editingClientId) {
        await axios.put(`${API}/admin/users/${editingClientId}`, { ...payload, password: payload.password || null }, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/admin/users`, payload, { headers: getAuthHeader() });
      }
      closeClientModal();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleToggleActive = async (c) => {
    const active = c.is_active === false;
    if (!window.confirm(active ? `Riattivare ${c.name}?` : `Disattivare ${c.name}? Non potrà più accedere all'area riservata né essere riconosciuto in chat. Pratiche e documenti restano conservati.`)) return;
    try {
      await axios.post(`${API}/admin/clients/${c.id}/active`, { active }, { headers: getAuthHeader() });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Operazione non riuscita');
    }
  };

  const handleDeleteClient = async (c) => {
    if (!window.confirm(`Eliminare definitivamente ${c.name}? L'operazione non si può annullare.`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${c.id}`, { headers: getAuthHeader() });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Eliminazione non riuscita');
    }
  };

  const handleInvite = async (userId) => {
    if (!window.confirm('Inviare al cliente il link per scegliere una nuova password?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/invite`, {}, { headers: getAuthHeader() });
      alert('Link di accesso inviato');
    } catch (err) {
      alert(err.response?.data?.detail || 'Invio non riuscito');
    }
  };

  const handleCreatePratica = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/pratiche`, praticaForm, { headers: getAuthHeader() });
      setShowPraticaModal(false);
      setPraticaForm({ client_id: '', titolo: '', tipo: '', descrizione: '', note_cliente: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleUpdateStatus = async (praticaId, newStatus) => {
    try {
      await axios.put(`${API}/admin/pratiche/${praticaId}`, { status: newStatus }, { headers: getAuthHeader() });
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedPratica) return;
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadDesc) formData.append('description', uploadDesc);
    
    try {
      await axios.post(`${API}/admin/pratiche/${selectedPratica.id}/documents`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setShowDocModal(false);
      setUploadFile(null);
      setUploadDesc('');
      fetchDocuments(selectedPratica.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const fetchDocuments = async (praticaId) => {
    try {
      const res = await axios.get(`${API}/admin/pratiche/${praticaId}/documents`, { headers: getAuthHeader() });
      setDocuments(res.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API}/admin/documents/${docId}`, { headers: getAuthHeader() });
      fetchDocuments(selectedPratica.id);
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }, { headers: getAuthHeader() });
      setShowPaymentModal(false);
      setPaymentForm({ pratica_id: '', description: '', amount: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella creazione del pagamento');
    }
  };

  const [remindingId, setRemindingId] = useState(null);

  const handleRemindPayment = async (paymentId) => {
    setRemindingId(paymentId);
    try {
      await axios.post(`${API}/admin/payments/${paymentId}/remind`, {}, { headers: getAuthHeader() });
      alert('Promemoria inviato al cliente');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nell\'invio del promemoria');
    } finally {
      setRemindingId(null);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Eliminare questa richiesta di pagamento?')) return;
    try {
      await axios.delete(`${API}/admin/payments/${paymentId}`, { headers: getAuthHeader() });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#283880]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-[#283880] text-white flex items-center justify-between px-4 shadow">
        <span className="font-['Playfair_Display'] font-bold">Admin Panel</span>
        <button onClick={() => setMenuOpen(true)} className="w-11 h-11 flex items-center justify-center relative" aria-label="Apri menu" data-testid="admin-mobile-menu-btn">
          <Menu className="w-6 h-6" />
          {attiPending > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-[#C9A227]" />}
        </button>
      </div>
      {menuOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMenuOpen(false)} data-testid="admin-menu-overlay" />}
      {/* Sidebar */}
      <div className={`fixed left-0 top-0 bottom-0 w-64 bg-[#283880] text-white p-4 pt-6 z-50 transition-transform duration-300 lg:translate-x-0 ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={() => setMenuOpen(false)} className="lg:hidden absolute top-4 right-3 w-10 h-10 flex items-center justify-center" aria-label="Chiudi menu" data-testid="admin-mobile-menu-close">
          <X className="w-5 h-5" />
        </button>
        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-xl font-bold">Admin Panel</h1>
          <p className="text-slate-400 text-xs mt-1">Accordini & Carillo</p>
        </div>
        
        <nav className="space-y-2">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: Home },
            { id: 'clients', label: 'Clienti', icon: Users },
            { id: 'pratiche', label: 'Pratiche', icon: FileText },
            { id: 'payments', label: 'Pagamenti', icon: CreditCard },
            { id: 'atti', label: 'Atti', icon: Upload },
            { id: 'richieste', label: 'Richieste accesso', icon: UserCheck },
            { id: 'faq', label: 'FAQ suggerite', icon: HelpCircle },
            { id: 'agenda', label: 'Agenda', icon: CalendarDays },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setMenuOpen(false); }}
              data-testid={`admin-tab-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id ? 'bg-white/10 text-[#C9A227]' : 'hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
              {item.id === 'atti' && attiPending > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-[#C9A227] text-white rounded-full px-2 py-0.5" data-testid="admin-atti-badge">{attiPending}</span>
              )}
              {item.id === 'richieste' && requestsPending > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-[#C9A227] text-white rounded-full px-2 py-0.5" data-testid="admin-requests-badge">{requestsPending}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="border-t border-white/20 pt-4 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#C9A227] rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate max-w-[160px]">{user?.email}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 rounded-lg"
            data-testid="admin-change-password-btn"
          >
            <Settings className="w-4 h-4" />
            Cambia password
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-white/5 rounded-lg"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
        </div>
      </div>
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      {/* Main Content */}
      <div className="lg:ml-64 p-4 pt-20 sm:p-6 sm:pt-20 lg:p-8">
        {/* Dashboard */}
        {activeTab === 'dashboard' && (
          <div>
            <h2 className="text-2xl font-bold text-[#283880] mb-6">Dashboard</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-8">
              {[
                { label: 'Clienti', value: stats.total_clients, icon: Users, color: 'bg-blue-500' },
                { label: 'Pratiche', value: stats.total_pratiche, icon: FileText, color: 'bg-green-500' },
                { label: 'In Corso', value: stats.pratiche_aperte, icon: Clock, color: 'bg-yellow-500' },
                { label: 'Da Firmare', value: stats.pratiche_da_firmare, icon: AlertCircle, color: 'bg-orange-500' }
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-3xl font-bold text-[#283880]">{stat.value || 0}</p>
                  <p className="text-slate-500 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
            
            <ChatStatsChart />
            <ChatTopicsPanel />

            {/* Recent Pratiche */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-[#283880] mb-4">Pratiche Recenti</h3>
              <div className="space-y-3">
                {pratiche.slice(0, 5).map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{p.titolo}</p>
                      <p className="text-xs text-slate-500">{p.client_name} • {p.numero_pratica}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusOptions.find(s => s.value === p.status)?.color}`}>
                      {statusOptions.find(s => s.value === p.status)?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#283880]">Clienti</h2>
              <Button onClick={() => setShowClientModal(true)} className="bg-[#C9A227] hover:bg-[#b8911f]" data-testid="new-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuovo Cliente
              </Button>
            </div>

            <div className="relative mb-4 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Cerca per nome, email, codice fiscale o P.IVA"
                className="pl-9 pr-9 bg-white" data-testid="client-search-input" />
              {clientSearch && (
                <button onClick={() => setClientSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="client-search-clear"><X className="w-4 h-4" /></button>
              )}
            </div>
            {clientSearch && (
              <p className="text-xs text-slate-500 mb-3" data-testid="client-search-count">{filteredClients.length} di {clients.length} clienti</p>
            )}

            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Nome</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">CF / P.IVA</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Telefono</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Accesso</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 && (
                    <tr><td colSpan={10} className="p-8 text-center text-sm text-slate-400" data-testid="client-search-empty">Nessun cliente trovato</td></tr>
                  )}
                  {filteredClients.map((client) => (
                    <tr key={client.id} className={`border-t hover:bg-slate-50 ${client.is_active === false ? 'opacity-60' : ''}`} data-testid={`client-row-${client.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#283880] rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <span className="font-medium">{client.name}</span>
                          {client.is_active === false && <span className="text-[10px] uppercase font-bold bg-slate-200 text-slate-600 rounded px-1.5 py-0.5" data-testid={`client-inactive-badge-${client.id}`}>Disattivato</span>}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-slate-600">{client.email}</td>
                      <td className="p-4 text-xs font-mono text-slate-600">{[client.codice_fiscale, client.partita_iva].filter(Boolean).join(' · ') || '-'}</td>
                      <td className="p-4 text-sm text-slate-600">{client.phone || '-'}</td>
                      <td className="p-4 text-sm text-slate-600">
                        {new Date(client.created_at).toLocaleDateString('it')}
                      </td>
                      <td className="p-4">
                        <button onClick={() => handleInvite(client.id)} className="text-xs text-[#283880] hover:text-[#C9A227] underline" data-testid={`client-invite-btn-${client.id}`}>Invia link accesso</button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditClient(client)} title="Modifica" className="p-2 rounded-md text-[#283880] hover:bg-[#283880]/10 transition-colors" data-testid={`client-edit-btn-${client.id}`}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleToggleActive(client)} title={client.is_active === false ? 'Riattiva' : 'Disattiva'} className="p-2 rounded-md text-amber-600 hover:bg-amber-50 transition-colors" data-testid={`client-toggle-active-btn-${client.id}`}>
                            {client.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                          </button>
                          {(praticheCount[client.id] || 0) === 0 && (
                            <button onClick={() => handleDeleteClient(client)} title="Elimina" className="p-2 rounded-md text-red-600 hover:bg-red-50 transition-colors" data-testid={`client-delete-btn-${client.id}`}><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pratiche Tab */}
        {activeTab === 'pratiche' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#283880]">Pratiche</h2>
              <Button onClick={() => setShowPraticaModal(true)} className="bg-[#C9A227] hover:bg-[#b8911f]">
                <Plus className="w-4 h-4 mr-2" /> Nuova Pratica
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {pratiche.map((p) => (
                <div key={p.id} className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-[#283880]">{p.titolo}</h3>
                      <p className="text-sm text-slate-500">{p.numero_pratica}</p>
                    </div>
                    <select
                      value={p.status}
                      data-testid={`pratica-status-${p.id}`}
                      onChange={(e) => handleUpdateStatus(p.id, e.target.value)}
                      className={`text-xs px-3 py-1 rounded-full border-0 ${statusOptions.find(s => s.value === p.status)?.color}`}
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-slate-400">Cliente</p>
                      <p className="font-medium">{p.client_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Tipo</p>
                      <p>{p.tipo}</p>
                    </div>
                  </div>

                  <AppointmentField pratica={p} onSaved={fetchData} />

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedPratica(p); fetchDocuments(p.id); setShowDocModal(true); }}
                    >
                      <Upload className="w-4 h-4 mr-1" /> Documenti
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'atti' && <AdminAttiTab onCountChange={setAttiPending} onDataChange={fetchData} />}
        {activeTab === 'richieste' && <AdminAccessTab onCountChange={setRequestsPending} onDataChange={fetchData} />}
        {activeTab === 'faq' && <AdminFaqTab />}
        {activeTab === 'agenda' && <AdminAgendaTab />}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-2xl font-bold text-[#283880]">Pagamenti Copie Atti</h2>
              <Button onClick={() => setShowPaymentModal(true)} className="bg-[#C9A227] hover:bg-[#b8911f] w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" /> Nuova Richiesta Pagamento
              </Button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Cliente</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Pratica</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Descrizione</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Importo</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Stato</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Data</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-slate-400">Nessuna richiesta di pagamento</td>
                    </tr>
                  ) : payments.map((p) => {
                    const pratica = pratiche.find(pr => pr.id === p.pratica_id);
                    const client = clients.find(c => c.id === p.client_id);
                    return (
                      <tr key={p.id} className="border-t hover:bg-slate-50">
                        <td className="p-4 text-sm">{client?.name || p.client_id}</td>
                        <td className="p-4 text-sm text-slate-600">{pratica?.numero_pratica || '-'}</td>
                        <td className="p-4 text-sm text-slate-600">{p.description}</td>
                        <td className="p-4 text-sm font-semibold">&euro; {p.amount?.toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            p.status === 'paid' ? 'bg-green-100 text-green-700' :
                            p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            p.status === 'initiated' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {p.status === 'paid' ? 'Pagato' : p.status === 'pending' ? 'In attesa' : p.status === 'initiated' ? 'Avviato' : p.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(p.created_at).toLocaleDateString('it')}
                          {p.reminders_sent > 0 && (
                            <div className="text-xs text-slate-400" data-testid={`payment-reminders-info-${p.id}`}>
                              {p.reminders_sent} promemoria · ultimo {new Date(p.last_reminder_at).toLocaleDateString('it')}
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {(p.status === 'pending' || p.status === 'initiated') && (
                              <button
                                onClick={() => handleRemindPayment(p.id)}
                                disabled={remindingId === p.id}
                                title="Invia promemoria ora"
                                data-testid={`payment-remind-btn-${p.id}`}
                                className="text-[#283880] hover:text-[#C9A227] disabled:opacity-50"
                              >
                                {remindingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                              </button>
                            )}
                            {p.status === 'paid' && (
                              <button
                                onClick={() => downloadReceipt(`${API}/admin/payments/${p.id}/receipt`).catch(() => alert('Ricevuta non disponibile'))}
                                title={`Scarica ricevuta ${p.receipt_number || ''}`}
                                data-testid={`payment-receipt-btn-${p.id}`}
                                className="text-[#283880] hover:text-[#C9A227]"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            )}
                            {p.status === 'pending' && (
                              <button onClick={() => handleDeletePayment(p.id)} className="text-red-500 hover:text-red-700" data-testid={`payment-delete-btn-${p.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" data-testid="client-modal-title">{editingClientId ? 'Modifica Cliente' : 'Nuovo Cliente'}</h3>
              <button onClick={closeClientModal} data-testid="client-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreateClient} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome *</label>
                <Input value={clientForm.name} onChange={(e) => setClientForm({...clientForm, name: e.target.value})} required data-testid="client-form-name" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input type="email" value={clientForm.email} onChange={(e) => setClientForm({...clientForm, email: e.target.value})} required data-testid="client-form-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{editingClientId ? 'Nuova password (facoltativa)' : 'Password'}</label>
                <PasswordInput value={clientForm.password} onChange={(e) => setClientForm({...clientForm, password: e.target.value})} placeholder={editingClientId ? 'Lasci vuoto per non cambiarla' : 'Lasci vuoto: il cliente riceve il link per sceglierla'} data-testid="client-form-password" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select value={clientForm.tipo_soggetto} onChange={(e) => setClientForm({...clientForm, tipo_soggetto: e.target.value})} className="w-full border rounded-md p-2" data-testid="client-form-tipo">
                  <option value="privato">Privato</option>
                  <option value="azienda">Azienda</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{clientForm.tipo_soggetto === 'azienda' ? 'Codice fiscale azienda (se diverso dalla P.IVA)' : 'Codice fiscale'}</label>
                <Input value={clientForm.codice_fiscale} onChange={(e) => setClientForm({...clientForm, codice_fiscale: e.target.value.toUpperCase()})} data-testid="client-form-cf" />
              </div>
              {clientForm.tipo_soggetto === 'azienda' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Partita IVA</label>
                  <Input value={clientForm.partita_iva} onChange={(e) => setClientForm({...clientForm, partita_iva: e.target.value})} data-testid="client-form-piva" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Telefono</label>
                <Input value={clientForm.phone} onChange={(e) => setClientForm({...clientForm, phone: e.target.value})} data-testid="client-form-phone" />
              </div>
              {!editingClientId && (
              <div>
                <label className="block text-sm font-medium mb-1">Ruolo</label>
                <select value={clientForm.role} onChange={(e) => setClientForm({...clientForm, role: e.target.value})} className="w-full border rounded-md p-2">
                  <option value="client">Cliente</option>
                  <option value="admin">Amministratore</option>
                </select>
              </div>
              )}
              <Button type="submit" className="w-full bg-[#283880]" data-testid="client-form-submit">{editingClientId ? 'Salva modifiche' : 'Crea Cliente'}</Button>
            </form>
          </div>
        </div>
      )}

      {/* Pratica Modal */}
      {showPraticaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Nuova Pratica</h3>
              <button onClick={() => setShowPraticaModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreatePratica} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Cliente *</label>
                <select value={praticaForm.client_id} onChange={(e) => setPraticaForm({...praticaForm, client_id: e.target.value})} className="w-full border rounded-md p-2" required>
                  <option value="">Seleziona cliente</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Titolo *</label>
                <Input value={praticaForm.titolo} onChange={(e) => setPraticaForm({...praticaForm, titolo: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo *</label>
                <select value={praticaForm.tipo} onChange={(e) => setPraticaForm({...praticaForm, tipo: e.target.value})} className="w-full border rounded-md p-2" required>
                  <option value="">Seleziona tipo</option>
                  {tipoOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note per il cliente</label>
                <Textarea value={praticaForm.note_cliente} onChange={(e) => setPraticaForm({...praticaForm, note_cliente: e.target.value})} />
              </div>
              <Button type="submit" className="w-full bg-[#283880]">Crea Pratica</Button>
            </form>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocModal && selectedPratica && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Documenti - {selectedPratica.titolo}</h3>
              <button onClick={() => setShowDocModal(false)}><X className="w-5 h-5" /></button>
            </div>
            
            {/* Document List */}
            <div className="mb-6 max-h-60 overflow-auto">
              {documents.length === 0 ? (
                <p className="text-slate-400 text-center py-4">Nessun documento</p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#283880]" />
                        <div>
                          <p className="text-sm font-medium">{doc.original_filename}</p>
                          <p className="text-xs text-slate-400">{(doc.file_size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteDocument(doc.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Form */}
            <form onSubmit={handleUploadDocument} className="space-y-4 border-t pt-4">
              <div>
                <label className="block text-sm font-medium mb-1">Carica nuovo documento</label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full border rounded-md p-2 text-sm"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione (opzionale)</label>
                <Input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} />
              </div>
              <Button type="submit" disabled={!uploadFile} className="w-full bg-[#C9A227]">
                <Upload className="w-4 h-4 mr-2" /> Carica Documento
              </Button>
            </form>
          </div>
        </div>
      )}
      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Nuova Richiesta Pagamento</h3>
              <button onClick={() => setShowPaymentModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreatePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Pratica *</label>
                <select value={paymentForm.pratica_id} onChange={(e) => setPaymentForm({...paymentForm, pratica_id: e.target.value})} className="w-full border rounded-md p-2" required>
                  <option value="">Seleziona pratica</option>
                  {pratiche.map((p) => <option key={p.id} value={p.id}>{p.numero_pratica} - {p.titolo} ({p.client_name})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione *</label>
                <Input 
                  value={paymentForm.description} 
                  onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})} 
                  placeholder="Es: Copia conforme atto n.1234"
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Importo (EUR) *</label>
                <Input 
                  type="number" 
                  step="0.01" 
                  min="0.50"
                  value={paymentForm.amount} 
                  onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})} 
                  placeholder="0.00"
                  required 
                />
              </div>
              <Button type="submit" className="w-full bg-[#283880]">
                <CreditCard className="w-4 h-4 mr-2" /> Crea Richiesta Pagamento
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
